import { useEffect, useRef } from 'react';
import { useThree } from '@react-three/fiber';
import * as THREE from 'three';

// Opt-in camera auto-fit (options.fitToModel). After the model mounts, measures the real
// bounding box of the rendered geometry and moves the camera along its existing view direction
// so the whole model is visible on first open — no screenshot-calibrated distance multipliers.
// Fits both the vertical extent (arch sagitta + layer stack) and the horizontal span against the
// camera's actual fov/aspect/zoom, retargets OrbitControls to the model centre and raises its
// maxDistance when the fitted distance would otherwise be clamped on first user interaction.
// Fits only ONCE per camera instance (first non-empty bbox) so re-renders (websocket/form
// updates passing a fresh data identity) never yank the camera back while the operator is
// zooming/orbiting. Latching per camera (not per mount) matters: on mount the canvas's default
// camera is active first and the custom <Camera> (with its own zoom) registers a moment later —
// that swap must trigger one corrective fit or the real camera keeps the unfitted position.
// Callers that remount the scene on real data changes (EditForm refreshKey) still get a re-fit.
export default function FitCameraToModel({ modelRef, controlsRef, data, margin = 1.2 }) {
  const { camera } = useThree();
  const fittedCameraRef = useRef(null);

  useEffect(() => {
    if (fittedCameraRef.current === camera) return;
    const obj = modelRef?.current;
    if (!obj || !camera) return;

    const box = new THREE.Box3().setFromObject(obj);
    if (box.isEmpty()) return;

    const size = box.getSize(new THREE.Vector3());
    const center = box.getCenter(new THREE.Vector3());
    if (size.x === 0 && size.y === 0 && size.z === 0) return;

    const tanHalfFov = Math.tan((camera.fov * Math.PI) / 360);
    const aspect = camera.aspect || 1;
    const zoom = camera.zoom || 1;
    // Visible half-height at distance d is d * tan(fov/2) / zoom, so the distance needed to
    // show halfExtent is halfExtent * zoom / tan(fov/2); horizontal gains the aspect factor.
    const distForHeight = ((size.y / 2) * zoom) / tanHalfFov;
    const distForWidth = ((size.x / 2) * zoom) / (tanHalfFov * aspect);
    const dist = Math.max(distForHeight, distForWidth) * margin + size.z / 2;

    const dir = camera.position.clone().sub(center);
    if (dir.lengthSq() === 0) {
      dir.set(0, 0, 1);
    } else {
      dir.normalize();
    }
    camera.position.copy(center.clone().addScaledVector(dir, dist));
    camera.updateProjectionMatrix();

    if (controlsRef?.current) {
      controlsRef.current.target.copy(center);
      if (controlsRef.current.maxDistance < dist * 2) {
        controlsRef.current.maxDistance = dist * 2;
      }
      controlsRef.current.update();
    }
    fittedCameraRef.current = camera;
  }, [modelRef, controlsRef, camera, data, margin]);

  return null;
}
